// Shared, lazily-created AudioContext for call ringtones. Browsers (especially
// mobile Safari) suspend a brand-new AudioContext until it's resumed inside a
// genuine user-gesture handler -- creating one fresh inside a useEffect that
// fires from a network poll or a state change (not a click) means it silently
// never produces sound. We create ONE context lazily and arm a one-time
// document-level gesture listener to resume it as soon as the user taps
// anywhere on the page, so it's already unlocked by the time a real ring
// needs to play.
let ctx = null;
let listenersAttached = false;

export function getRingContext() {
  if (!ctx) {
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { return null; }
  }
  if (ctx.state === "suspended") { ctx.resume().catch(() => {}); }
  armUnlock();
  return ctx;
}

// NOTE: listeners are attached ONCE but never removed (no `{ once: true }`)
// on the actual DOM registration. Mobile browsers re-suspend an AudioContext
// after the tab is backgrounded / screen locks, which happens routinely
// between calls -- a listener that fires-then-detaches after the very first
// tap can only ever unlock the context one time per page load, so the ring
// goes silent again on the next real suspension with nothing left to
// re-resume it. Keeping the listeners live lets any later genuine gesture
// re-resume the context whenever the browser has re-suspended it.
function armUnlock() {
  if (listenersAttached || typeof document === "undefined") return;
  listenersAttached = true;
  const unlock = () => {
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  };
  document.addEventListener("pointerdown", unlock);
  document.addEventListener("click", unlock);
}
